// src/pages/ProductCustomization.js
import { fabric } from "fabric";
import Lottie from "lottie-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cartAnimation from "../assets/cartAnimation.json";
import cartSound from "../assets/Paper-Cart.wav";
import ProductHeader from "../components/ProductHeader";
import ProductLoadingSkeleton from "../components/ProductLoadingSkeleton";
import { useCart } from "../context/CartContext";
import axios from "../utils/axiosHandler";

const ProductCustomization = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [canvas, setCanvas] = useState(null);
  // const {productName, id } = useParams();
  const { productSlug } = useParams();
  const [productId, setProductId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [orderDescription, setOrderDescription] = useState("");
  const canvasRef = useRef(null);
  // const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [categoryTemplates, setCategoryTemplates] = useState([]);
  const [selectedProductImage, setSelectedProductImage] = useState(0);
  const [currentUnitPrice, setCurrentUnitPrice] = useState(0);
  const [fullImagePath, setFullImagePath] = useState(null);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [selectedBackgroundColor, setSelectedBackgroundColor] =
    useState("#EEF2FF");
  const [selectedSecondaryColor, setSelectedSecondaryColor] =
    useState("#EDE9FE");
  // holds the area of the product image on the canvas
  const [productImageArea, setProductImageArea] = useState(null);
  const [remindPopup, setRemindPopup] = useState({ show: false, message: "" });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [formClosing, setFormClosing] = useState(false);

  // Template field states
  const [requiredFields, setRequiredFields] = useState([]);
  const [fieldInputs, setFieldInputs] = useState({});
  const [templateGroups, setTemplateGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [templatesPerPage, setTemplatesPerPage] = useState(3);
  const [showAllTemplates, setShowAllTemplates] = useState(false);

  // Customization states
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [fillColor, setFillColor] = useState("transparent");
  const [selectedFontSize, setSelectedFontSize] = useState("20");
  const [selectedFontFamily, setSelectedFontFamily] = useState("Arial");
  const [customText, setCustomText] = useState("");
  const [customizations, setCustomizations] = useState({});
  const [canvasVersion, setCanvasVersion] = useState(0);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [notifyContact, setNotifyContact] = useState({ email: "", phone: "" });
  const [showNotifyForm, setShowNotifyForm] = useState(false);
  const customImageInputRef = useRef(null);
  const [animatingUpload, setAnimatingUpload] = useState(false);
  const [animatingCustomize, setAnimatingCustomize] = useState(false);
  const [animatingNotify, setAnimatingNotify] = useState(false);

  // state to store customizations for each product image
  const [imageCustomizations, setImageCustomizations] = useState({});

  // NEW: State to toggle design view mode (null, "upload", or "customize")
  const [designMode, setDesignMode] = useState(null);
  // NEW: State to control display of the Add-To-Cart confirmation modal
  const [showCartModal, setShowCartModal] = useState(false);

  const cartAudio = new Audio(cartSound);

  const [showStickyUpload, setShowStickyUpload] = useState(false);
  const topRef = useRef(null);
  const [showUploadOverlay, setShowUploadOverlay] = useState(false);
  const [showTemplatesPopup, setShowTemplatesPopup] = useState(false);
  const [showGroupSelection, setShowGroupSelection] = useState(true);
  const [currentGroupName, setCurrentGroupName] = useState("");

  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true); // Ensure loading is true at the start

        const productRes = await axios.get(
          `/api/products/by-slug/${productSlug}`,

        );

        setSelectedProduct(productRes.data);
        setProductId(productRes.data._id);

        // Get templates
        const templatesRes = await axios.get("/api/templates");

        const matchingTemplates = templatesRes.data.filter((template) => {
          // Skip templates without a category
          if (!template.category) return false;

          // Get template category ID safely
          const templateCategoryId =
            template.category._id ||
            (typeof template.category === "string" ? template.category : null);

          // Get product category ID safely
          const productCategoryId =
            productRes.data.category && productRes.data.category._id
              ? productRes.data.category._id
              : typeof productRes.data.category === "string"
                ? productRes.data.category
                : null;

          // Only match if both IDs exist
          return (
            templateCategoryId &&
            productCategoryId &&
            templateCategoryId === productCategoryId
          );
        });

        setCategoryTemplates(matchingTemplates);
        setFilteredTemplates(matchingTemplates);

        const uniqueGroups = [];

        matchingTemplates.forEach((template) => {
          if (
            template.group &&
            typeof template.group === "object" &&
            template.group._id &&
            !uniqueGroups.some((g) => g._id === template.group._id)
          ) {
            uniqueGroups.push(template.group);
          }
        });

        setTemplateGroups(uniqueGroups);

        // Set a minimum loading time of 1.5 seconds to show the skeleton
        setTimeout(() => {
          setIsLoading(false);
        }, 1500);
      } catch (error) {
        console.error("Error fetching data:", error);
        setTimeout(() => {
          setIsLoading(false);
        }, 1500);
      }
    };

    if (productSlug) {
      fetchData();
    }

    return () => {
      // If the component unmounts during loading, ensure we don't try to update state
      setIsLoading(false);
    };
  }, [productSlug, navigate]);

  useEffect(() => {
    if (!isLoading && selectedProduct && canvas) {
      // If we have a product and canvas, but the canvas is empty,
      // load the default product image
      if (selectedProduct.images && selectedProduct.images.length > 0) {
        const defaultImage = selectedProduct.images[0];
        let defaultImageData;

        // Handle new file-based structure
        if (defaultImage.url) {
          defaultImageData = defaultImage.url;
        }
        if (defaultImage.path) {
          defaultImageData = defaultImage.path.startsWith("/api")
            ? defaultImage.path
            : `/api${defaultImage.path}`;
        } else {
          console.error("No valid image data found:", defaultImage);
          return;
        }

        const defaultImageIndex = 0;

        // This will ensure the product image is loaded once data is ready
        setTimeout(() => {
          loadProductImage(defaultImageData, defaultImageIndex);
        }, 100); // Small delay to ensure the DOM has updated
      }
    }
  }, [isLoading, selectedProduct, canvas]);

  const toggleTemplatesPopup = () => {
    setShowTemplatesPopup((prev) => !prev);
  };

  // useEffect(() => {
  //   const searchParams = new URLSearchParams(window.location.search);
  //   const id = searchParams.get('id');
  //   if (id) {
  //     setProductId(id);
  //   }
  // }, []);

  // useEffect(() => {
  //   if (id) {
  //     setProductId(id);
  //   }
  // }, [id]);

  // Reorder images: show the 2nd, 3rd, … images first, then the 1st image at the end
  // const reorderedImages =
  //   selectedProduct?.images && selectedProduct.images.length > 1
  //     ? [...selectedProduct.images.slice(1), selectedProduct.images[0]]
  //     : selectedProduct?.images || [];

  // Process images to ensure they have proper URLs
  const processedImages =
    selectedProduct?.images?.map((image) => {
      if (image.url) {
        return { ...image, displayUrl: image.url };
      } else if (image.path) {
        return { ...image, displayUrl: `/api${image.path}` };
      } else if (image.data) {
        return { ...image, displayUrl: image.data };
      }
      return image;
    }) || [];

  const reorderedImages =
    processedImages && processedImages.length > 1
      ? processedImages
      : processedImages || [];
  // newer
  // const reorderedImages =
  //   selectedProduct?.images && selectedProduct.images.length > 1
  //     ? selectedProduct?.images || []
  //     : selectedProduct?.images || [];

  const updateCanvasVersion = () => {
    setCanvasVersion((prev) => prev + 1);
  };

  const fontFamilies = [
    "Arial",
    "Times New Roman",
    "Courier New",
    "Georgia",
    "Verdana",
    "Helvetica",
    "Palatino",
    "Garamond",
    "Bookman",
    "Tahoma",
  ];

  const getBundleOptions = () => {
    if (
      !selectedProduct?.pricingTiers ||
      selectedProduct.pricingTiers.length === 0
    ) {
      return [];
    }
    // Map each tier to its maxQuantity if exists, otherwise minQuantity
    const options = selectedProduct.pricingTiers.map((tier) =>
      tier.maxQuantity ? tier.maxQuantity : tier.minQuantity
    );
    // Remove duplicates and sort options
    const uniqueOptions = Array.from(new Set(options)).sort((a, b) => a - b);
    // Always include 20000 if not already present
    if (!uniqueOptions.includes(20000)) {
      uniqueOptions.push(20000);
      uniqueOptions.sort((a, b) => a - b);
    }
    return uniqueOptions;
  };

  const handleRemindMe = async () => {
    try {
      const data = {};
      if (notifyContact.email) data.email = notifyContact.email;
      if (notifyContact.phone) data.phone = notifyContact.phone;
      if (!data.email && !data.phone) {
        setRemindPopup({
          show: true,
          message: "Please enter your email and/or phone.",
        });
        return;
      }
      const headers = isUserLoggedIn
        ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
        : {};
      const response = await axios.post(
        `/api/products/${productId}/remind-me`,
        data,
        { headers }
      );
      setRemindPopup({ show: true, message: response.data.message });
    } catch (error) {
      console.error("Error setting reminder:", error);
      setRemindPopup({
        show: true,
        message: "Failed to set reminder. Please try again.",
      });
    }
  };

  const calculateUnitPrice = (qty) => {
    if (!selectedProduct?.pricingTiers?.length) {
      return selectedProduct?.basePrice || 0;
    }

    const applicableTier = selectedProduct.pricingTiers
      .sort((a, b) => b.minQuantity - a.minQuantity)
      .find(
        (tier) =>
          qty >= tier.minQuantity &&
          (!tier.maxQuantity || qty <= tier.maxQuantity)
      );

    return applicableTier ? applicableTier.price : selectedProduct.basePrice;
  };

  const handleQuantityChange = (newQty) => {
    // Enforce minimum order
    const minOrder = selectedProduct?.minimumOrder || 1;
    if (newQty < minOrder) {
      alert(`Minimum order quantity is ${minOrder}`);
      newQty = minOrder;
    }

    // Check if product is in stock
    if (!selectedProduct?.inStock) {
      alert("This product is currently out of stock");
      return;
    }

    setQuantity(newQty);
    setCurrentUnitPrice(calculateUnitPrice(newQty));
  };

  useEffect(() => {
    // Reset animation states when design mode changes
    if (designMode !== "upload") {
      setAnimatingUpload(false);
    }
    if (designMode !== "customize") {
      setAnimatingCustomize(false);
    }
    if (!showNotifyForm) {
      setAnimatingNotify(false);
    }
  }, [designMode]);

  useEffect(() => {
    if (!canvas) return;

    const handleResize = () => {
      const containerElement = document.getElementById("canvas-container");
      if (!containerElement) return;

      const windowWidth = window.innerWidth;
      let targetWidth, targetHeight;

      if (windowWidth > 1550) {
        targetWidth = targetHeight = 600;
      } else if (windowWidth >= 1280 && windowWidth <= 1550) {
        targetWidth = targetHeight = 500;
      } else if (windowWidth >= 1224 && windowWidth < 1280) {
        targetWidth = targetHeight = 400;
      } else if (windowWidth >= 768 && windowWidth < 1224) {
        targetWidth = targetHeight = 300;
      } else if (windowWidth >= 660 && windowWidth < 768) {
        targetWidth = windowWidth - 170;
        targetHeight = targetWidth;
      } else {
        targetWidth = windowWidth - 100;
        targetHeight = targetWidth;
      }

      // Update container size
      containerElement.style.width = `${targetWidth}px`;
      containerElement.style.height = `${targetHeight}px`;

      // Only update the canvas if dimensions have changed
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.setWidth(targetWidth);
        canvas.setHeight(targetHeight);
        canvas.calcOffset();
        canvas.renderAll();

        // Reposition background image and update clipping if needed
        if (canvas.backgroundImage) {
          const bgImg = canvas.backgroundImage;
          const scale =
            Math.min(canvas.width / bgImg.width, canvas.height / bgImg.height) *
            0.9;
          const left = (canvas.width - bgImg.width * scale) / 2;
          const top = (canvas.height - bgImg.height * scale) / 2;
          bgImg.set({ scaleX: scale, scaleY: scale, left, top });

          const clipRect = new fabric.Rect({
            left,
            top,
            width: bgImg.width * scale,
            height: bgImg.height * scale,
            absolutePositioned: true,
          });

          canvas.getObjects().forEach((obj) => {
            if (obj !== canvas.backgroundImage) {
              obj.clipPath = clipRect;
            }
          });

          setProductImageArea({
            x: left,
            y: top,
            width: bgImg.width * scale,
            height: bgImg.height * scale,
          });
        }

        canvas.renderAll();
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // call once on mount

    return () => window.removeEventListener("resize", handleResize);
  }, [canvas]);

  useEffect(() => {
    if (isLoading) return;

    const containerElement = document.getElementById("canvas-container");
    if (!containerElement) return;
    containerElement.innerHTML = "";

    const canvasElement = document.createElement("canvas");
    canvasElement.id = "fabric-canvas";
    canvasElement.className = "border rounded-lg";
    containerElement.appendChild(canvasElement);

    // Determine canvas width based on screen size
    const isXlScreen = window.innerWidth >= 1280;
    const canvasWidth = isXlScreen
      ? 500
      : Math.min(containerElement.offsetWidth, 500);

    const fabricCanvas = new fabric.Canvas(canvasElement, {
      width: canvasWidth,
      height: canvasWidth, // keep it square
      backgroundColor: "#ffffff",
    });

    // Set up fabric event listeners
    fabricCanvas.selection = false;
    fabricCanvas.lowerCanvasEl.style.touchAction = "pan-y";

    fabricCanvas.on("mouse:down", function (opt) {
      const evt = opt.e;
      if (evt.altKey === true) {
        this.isDragging = true;
        this.selection = false;
        this.lastPosX = evt.clientX;
        this.lastPosY = evt.clientY;
      }
    });

    fabricCanvas.on("object:added", function (e) {
      const obj = e.target;
      obj.set({
        selectable: true,
        hasControls: true,
        hasBorders: true,
        selection: true,
      });
      updateCanvasVersion();
    });

    fabricCanvas.on("selection:created", (e) => {
      console.log("Selection created:", e.target);
      handleObjectSelected(e);
    });
    fabricCanvas.on("selection:updated", (e) => {
      console.log("Selection updated:", e.target);
      handleObjectSelected(e);
    });
    fabricCanvas.on("selection:cleared", (e) => {
      console.log("Selection cleared");
      handleSelectionCleared();
    });
    fabricCanvas.on("object:modified", (e) => {
      handleObjectModified(e);
      updateCanvasVersion();
    });

    setCanvas(fabricCanvas);

    // Load default product image if available
    if (selectedProduct?.images && selectedProduct.images.length > 0) {
      const defaultImage = selectedProduct.images[0];
      let defaultImageData;

      // Handle new file-based structure
      if (defaultImage.url) {
        defaultImageData = defaultImage.url;
      } else if (defaultImage.path) {
        defaultImageData = `/api${defaultImage.path}`;
      } else if (defaultImage.data) {
        // Fallback to old base64 format
        defaultImageData = defaultImage.data;
      }

      if (defaultImageData) {
        setTimeout(() => {
          loadProductImage(defaultImageData, 0);
        }, 100);
      }
    }
  }, [isLoading, selectedProduct]);

  // Add this function to handle page navigation events (back button fix)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clean up canvas properly before navigating away
      if (canvas) {
        try {
          canvas.__eventListeners = {};
          canvas.upperCanvasEl = null;
          canvas.lowerCanvasEl = null;
          canvas.cacheCanvasEl = null;
          canvas.contextCache = null;
          canvas.wrapperEl = null;
        } catch (error) {
          console.error("Error cleaning up canvas before unload:", error);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [canvas]);

  useEffect(() => {
    if (selectedProduct) {
      // Set the default quantity based on price tiers
      if (
        selectedProduct.pricingTiers &&
        selectedProduct.pricingTiers.length > 0
      ) {
        // Sort tiers by minQuantity to ensure we get the first tier
        const sortedTiers = [...selectedProduct.pricingTiers].sort(
          (a, b) => a.minQuantity - b.minQuantity
        );
        const firstTier = sortedTiers[0];

        // Set quantity to maxQuantity of first tier if it exists, otherwise use minQuantity
        const defaultQuantity = firstTier.maxQuantity || firstTier.minQuantity;
        setQuantity(defaultQuantity);
        setCurrentUnitPrice(calculateUnitPrice(defaultQuantity));
      } else {
        // If no pricing tiers, set to 1 or minimum order
        const minOrder = selectedProduct.minimumOrder || 1;
        setQuantity(minOrder);
        setCurrentUnitPrice(calculateUnitPrice(minOrder));
      }
    }
  }, [selectedProduct]);

  const handleGroupSelect = (groupId, groupName) => {
    filterTemplatesByGroup(groupId);
    setShowGroupSelection(false);
    setCurrentGroupName(groupName || "All Templates");
  };

  const backToGroups = () => {
    setShowGroupSelection(true);
  };

  // Update your filterTemplatesByGroup function
  const filterTemplatesByGroup = (groupId) => {
    setSelectedGroupId(groupId);
    setShowAllTemplates(false); // Reset show all status when changing groups

    if (groupId === null) {
      // Show all templates
      setFilteredTemplates(categoryTemplates);
    } else {
      // Filter templates by the selected group
      setFilteredTemplates(
        categoryTemplates.filter(
          (template) =>
            template.group &&
            ((typeof template.group === "object" &&
              template.group._id === groupId) ||
              (typeof template.group === "string" &&
                template.group === groupId))
        )
      );
    }
  };

  const handleShowMore = () => {
    setShowAllTemplates(true);
  };

  const getDisplayedTemplates = () => {
    if (showAllTemplates) {
      // Mark templates beyond the initial set as "new" for animation
      return filteredTemplates.map((template, index) => ({
        ...template,
        isNewlyAdded: index >= templatesPerPage,
      }));
    } else {
      return filteredTemplates.slice(0, templatesPerPage).map((template) => ({
        ...template,
        isNewlyAdded: false,
      }));
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      if (!isMobile) {
        setShowStickyUpload(false);
      } else if (designMode === "upload" || designMode === "customize") {
        setShowStickyUpload(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [designMode]);

  const loadProductImage = (imageData, imageIndex) => {
    if (!canvas) return;

    // Save current canvas state for the current product image
    if (selectedProductImage !== null && canvas) {
      const currentObjects = canvas
        .getObjects()
        .filter((obj) => obj !== canvas.backgroundImage);
      if (currentObjects.length > 0) {
        setImageCustomizations((prev) => ({
          ...prev,
          [selectedProductImage]: {
            objects: canvas.toJSON(["data", "id", "type", "originalFilePath"])
              .objects,
            timestamp: Date.now(),
          },
        }));
      }
    }

    // Determine the image URL - handle both old string format and new file path format
    let imageUrl;
    if (typeof imageData === "string") {
      if (imageData.startsWith("data:")) {
        // Old base64 format
        imageUrl = imageData;
      }
      if (imageData.startsWith("/")) {
        imageUrl = imageData.startsWith("/api")
          ? imageData
          : `/api${imageData}`;
      }
    } else if (imageData && imageData.url) {
      // New structure with url property
      imageUrl = imageData.url;
    } else if (imageData && imageData.path) {
      imageUrl = imageData.path.startsWith("/api")
        ? imageData.path
        : `/api${imageData.path}`;
    }

    // Load the new product background image
    //  fabric.Image.fromURL(imageData, (img) => {
    fabric.Image.fromURL(imageUrl, (img) => {
      canvas.clear();
      const scale =
        Math.min(canvas.width / img.width, canvas.height / img.height) * 0.9;
      const computedWidth = img.width * scale;
      const computedHeight = img.height * scale;
      const left = (canvas.width - computedWidth) / 2;
      const top = (canvas.height - computedHeight) / 2;

      img.set({
        scaleX: scale,
        scaleY: scale,
        left: left,
        top: top,
        selectable: false,
        evented: false,
      });

      canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));

      // Set the clipping area to match the product image area
      setProductImageArea({
        x: left,
        y: top,
        width: computedWidth,
        height: computedHeight,
      });

      // Restore previous customizations for this product image if they exist
      if (imageIndex !== undefined && imageCustomizations[imageIndex]) {
        const savedState = imageCustomizations[imageIndex];

        // Check if we have objects to restore
        if (savedState.objects && savedState.objects.length > 0) {
          fabric.util.enlivenObjects(savedState.objects, (enlivenedObjects) => {
            enlivenedObjects.forEach((obj) => {
              // Apply clipping to each object
              obj.clipPath = new fabric.Rect({
                left: left,
                top: top,
                width: computedWidth,
                height: computedHeight,
                absolutePositioned: true,
              });

              // Add the object to canvas
              canvas.add(obj);
            });

            canvas.renderAll();
          });
        }
      }
    });
  };

  const handleTemplateChange = (template) => {
    if (!canvas || !template) return;

    setSelectedTemplate(template);
    setRequiredFields(template.requiredFields || []);
    setFieldInputs({});
    setCustomizations({});

    // Keep background image
    const bgImage = canvas.backgroundImage;
    canvas.clear();
    canvas.setBackgroundImage(bgImage, canvas.renderAll.bind(canvas));

    // Load template elements
    let elementsData = template.elements;
    if (typeof template.elements === "string") {
      try {
        elementsData = JSON.parse(template.elements);
      } catch (error) {
        console.error("Error parsing template elements:", error);
        return;
      }
    }

    if (elementsData && elementsData.objects) {
      elementsData.objects.forEach((obj) => {
        fabric.util.enlivenObjects([obj], (enlivenedObjects) => {
          const enlivenedObject = enlivenedObjects[0];

          // Keep required field IDs and placeholder properties
          if (
            enlivenedObject.data?.isPlaceholder ||
            enlivenedObject.data?.required
          ) {
            enlivenedObject.set({
              hasControls: true,
              lockUniScaling: false,
              lockRotation: false,
              selectable: true,
            });
          }

          // Apply clipping to template elements
          if (productImageArea) {
            enlivenedObject.clipPath = new fabric.Rect({
              left: productImageArea.x,
              top: productImageArea.y,
              width: productImageArea.width,
              height: productImageArea.height,
              absolutePositioned: true,
            });
          }

          canvas.add(enlivenedObject);
          canvas.renderAll();
        });
      });
    }

    // Save this as the new state for the current product image
    if (selectedProductImage !== null) {
      setImageCustomizations((prev) => ({
        ...prev,
        [selectedProductImage]: {
          objects: canvas.toJSON(["data", "id", "type", "originalFilePath"])
            .objects,
          timestamp: Date.now(),
        },
      }));
    }
  };

  const handleObjectSelected = (event) => {
    let obj;

    // Handle both direct object passing and event passing
    if (event.target) {
      obj = event.target;
    } else if (event.selected && event.selected[0]) {
      obj = event.selected[0];
    } else {
      return;
    }

    console.log("Object selected:", {
      type: obj.type,
      text: obj.text,
      fontFamily: obj.fontFamily,
      fontSize: obj.fontSize,
      fill: obj.fill,
    });

    setSelectedObject(obj);
    setSelectedColor(obj.fill || "#000000");
    setFillColor(obj.fill === "transparent" ? "transparent" : obj.fill);

    if (obj.type === "i-text" || obj.type === "text") {
      setSelectedFontSize(obj.fontSize?.toString() || "20");
      setSelectedFontFamily(obj.fontFamily || "Arial");
      setCustomText(obj.text || "");
    }

    // Force a re-render
    updateCanvasVersion();
  };

  const handleObjectModified = (e) => {
    const obj = e.target;
    if (!obj || !obj.data?.id) return;

    // For image objects, use the original file path if it exists.
    const imageContent =
      obj.type === "image"
        ? obj.data.originalFilePath ||
        obj.toDataURL({ format: "png", quality: 1, multiplier: 4 })
        : null;

    setCustomizations((prev) => ({
      ...prev,
      [obj.data.id]: {
        type: obj.data.type || "text",
        content:
          obj.type === "i-text" || obj.type === "text"
            ? obj.text
            : imageContent,
        image: obj.type === "image" ? imageContent : null,
        properties: {
          fontSize: obj.fontSize,
          fontFamily: obj.fontFamily,
          fill: obj.fill,
          position: { x: obj.left, y: obj.top },
          scale: { x: obj.scaleX, y: obj.scaleY },
        },
      },
    }));

    if (obj.data?.required) {
      setFieldInputs((prev) => ({
        ...prev,
        [obj.data.id]:
          obj.type === "i-text" || obj.type === "text"
            ? obj.text
            : obj.data.originalFilePath
              ? obj.data.originalFilePath
              : obj.toDataURL(),
      }));
    }
  };

  const handleSelectionCleared = () => {
    setSelectedObject(null);
    setSelectedColor("#000000");
    setFillColor("transparent");
    setSelectedFontSize("20");
    setSelectedFontFamily("Arial");
    setCustomText("");
  };

  const handleFieldInput = async (fieldId, value, type) => {
    if (!canvas) return;

    const fields = canvas
      .getObjects()
      .filter((obj) => obj.data?.id === fieldId);
    if (!fields.length) return;

    const field = fields[0];

    if ((type === "image" || type === "logo") && value instanceof File) {
      if (!productImageArea) {
        alert("Please select a product image first");
        return;
      }
      const originalDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(value);
      });

      setFieldInputs((prev) => ({
        ...prev,
        [fieldId]: {
          displayData: originalDataUrl, // For preview
          originalFile: value, // Keep original file
        },
      }));

      fabric.Image.fromURL(originalDataUrl, (img) => {
        const scale = Math.min(
          field.width / img.width,
          field.height / img.height
        );

        img.set({
          left: field.left,
          top: field.top,
          scaleX: scale,
          scaleY: scale,
          data: {
            ...field.data,
            originalFile: value,
          },
        });
        img.clipPath = new fabric.Rect({
          left: productImageArea.x,
          top: productImageArea.y,
          width: productImageArea.width,
          height: productImageArea.height,
          absolutePositioned: true,
        });

        canvas.remove(field);
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
      });
    }

    if (type === "text" && (field.type === "i-text" || field.type === "text")) {
      field.set("text", value);
      setFieldInputs((prev) => ({ ...prev, [fieldId]: value }));
      handleObjectModified({ target: field });
    } else if ((type === "image" || type === "logo") && value instanceof File) {
      if (!productImageArea) {
        alert("Please select a product image first");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        fabric.Image.fromURL(e.target.result, (img) => {
          const scale = Math.min(
            (field.width * field.scaleX) / img.width,
            (field.height * field.scaleY) / img.height
          );

          img.set({
            left: field.left,
            top: field.top,
            scaleX: scale,
            scaleY: scale,
            data: field.data,
          });

          img.clipPath = new fabric.Rect({
            left: productImageArea.x,
            top: productImageArea.y,
            width: productImageArea.width,
            height: productImageArea.height,
            absolutePositioned: true,
          });

          canvas.remove(field);
          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();

          setFieldInputs((prev) => ({ ...prev, [fieldId]: e.target.result }));
          handleObjectModified({ target: img });
        });
      };
      reader.readAsDataURL(value);
    }

    canvas.renderAll();
  };

  const handleCustomText = () => {
    if (!canvas || !customText) return;
    if (!productImageArea) {
      alert("Please select a product image first");
      return;
    }

    const text = new fabric.IText(customText, {
      left: canvas.width / 2,
      top: canvas.height / 2,
      fontSize: parseInt(selectedFontSize),
      fontFamily: selectedFontFamily,
      fill: selectedColor,
      originX: "center",
      originY: "center",
      selectable: true,
      hasControls: true,
      hasBorders: true,
      data: { type: "custom-text", id: `custom_${Date.now()}` },
    });
    // Apply clipping so the text is only visible within the product image area
    text.clipPath = new fabric.Rect({
      left: productImageArea.x,
      top: productImageArea.y,
      width: productImageArea.width,
      height: productImageArea.height,
      absolutePositioned: true,
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
    setCustomText("");
    handleObjectSelected({ target: text });
    updateCanvasVersion();
  };

  const handleCustomImage = async (e) => {
    const file = e.target.files[0];
    if (!file || !canvas) return;
    if (!productImageArea) {
      alert("Please select a product image first");
      return;
    }

    // file validation
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/svg+xml",
      "image/bmp",
      "image/tiff",
      "image/x-tiff",
      "image/heic",
      "image/heif",
      "image/x-heic",
      "image/x-heif",
    ];
    if (!validTypes.includes(file.type)) {
      alert("Please upload a valid image file (JPEG, PNG, GIF, WEBP, or SVG)");
      return;
    }

    // size validation (e.g., 50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      alert("File is too large. Please upload an image smaller than 50MB.");
      return;
    }

    try {
      // Create form data
      const formData = new FormData();
      formData.append("image", file);

      // Upload image
      const response = await axios.post("/api/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const { filePath } = response.data;

      // Create preview and add to canvas
      const reader = new FileReader();
      reader.onload = (event) => {
        fabric.Image.fromURL(event.target.result, (img) => {
          img.data = {
            type: "custom-image",
            id: `custom_${Date.now()}`,
            originalFilePath: filePath,
          };

          // Apply clipping so the image is only visible within the product image area
          img.clipPath = new fabric.Rect({
            left: productImageArea.x,
            top: productImageArea.y,
            width: productImageArea.width,
            height: productImageArea.height,
            absolutePositioned: true,
          });

          img.scaleToWidth(200);
          img.set({
            left: canvas.width / 2,
            top: canvas.height / 2,
            originX: "center",
            originY: "center",
            cornerSize: 12,
            cornerColor: "#ffffff",
            cornerStrokeColor: "#333333",
            transparentCorners: false,
            cornerStyle: "circle",
          });
          canvas.add(img);
          canvas.setActiveObject(img);
          setSelectedObject(img); // <-- This line ensures the design tools appear for the new image.
          setUploadedImages((prev) => [...prev, file]);
          canvas.renderAll();
          handleObjectModified({ target: img });
          updateCanvasVersion();
        });
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Failed to upload image. Please try again.");
    }
  };

  const updateSelectedObject = () => {
    if (!canvas || !selectedObject) return;

    const updates = {
      fill: selectedColor === "transparent" ? "transparent" : selectedColor,
    };

    if (selectedObject.type === "i-text" || selectedObject.type === "text") {
      updates.fontSize = parseInt(selectedFontSize);
      updates.fontFamily = selectedFontFamily;
    }

    selectedObject.set(updates);
    canvas.renderAll();
    handleObjectModified({ target: selectedObject });
  };

  const handleDelete = () => {
    if (!canvas || !selectedObject) return;

    // Prevent deletion of required template fields
    if (selectedObject.data?.required) {
      alert("Cannot delete required template elements");
      return;
    }

    if (selectedObject.data?.id) {
      setCustomizations((prev) => {
        const newCustomizations = { ...prev };
        delete newCustomizations[selectedObject.data.id];
        return newCustomizations;
      });
    }

    canvas.remove(selectedObject);
    canvas.discardActiveObject();
    canvas.renderAll();
    handleSelectionCleared();
  };

  const handleImageDelete = (index) => {
    // Get current objects on canvas
    const canvasObjects = canvas.getObjects();

    // Filter out the object matching this image if possible
    // This is a simplified approach - you might need more sophisticated matching
    const uploadedImage = uploadedImages[index];

    // Remove from canvas if found
    canvasObjects.forEach((obj) => {
      if (
        obj.data?.originalFilePath &&
        obj.data.originalFilePath.includes(uploadedImage.name)
      ) {
        canvas.remove(obj);
      }
    });

    // Update the uploaded images array
    const newImages = [...uploadedImages];
    newImages.splice(index, 1);
    setUploadedImages(newImages);

    // Render canvas
    canvas.renderAll();
    updateCanvasVersion();
  };

  // NEW: Open the cart modal after validating product in-stock and quantity
  const openCartModal = () => {
    if (!selectedProduct || !canvas) return;

    if (!selectedProduct.inStock) {
      alert("This product is currently out of stock");
      return;
    }

    if (quantity < (selectedProduct?.minimumOrder || 1)) {
      alert(`Minimum order quantity is ${selectedProduct.minimumOrder}`);
      return;
    }

    setShowCartModal(true);
  };

  // NEW: Refactored function to add item to cart (same logic as before)
  const addItemToCart = async () => {
    const customElements = canvas
      .getObjects()
      .filter((obj) => obj !== canvas.backgroundImage);

    // Process custom fields from canvas objects
    const customFields = customElements.map((obj) => {
      if (obj.data?.type === "image" || obj.data?.type === "logo") {
        return {
          fieldId: obj.data?.id || `custom_${Date.now()}`,
          type: obj.data.type,
          imageUrl: obj.data?.originalFilePath || null,
          content:
            obj.data && obj.data.originalFilePath
              ? obj.data.originalFilePath
              : obj.toDataURL({ format: "png", quality: 1, multiplier: 4 }),
          properties: {
            fontSize: null,
            fontFamily: null,
            fill: null,
            position: { x: obj.left || 0, y: obj.top || 0 },
            scale: { x: obj.scaleX || 1, y: obj.scaleY || 1 },
          },
        };
      }
      if (obj.type === "text" || obj.type === "i-text") {
        return {
          fieldId: obj.data?.id || `custom_${Date.now()}`,
          type: "text",
          content: obj.text,
          properties: {
            fontSize: obj.fontSize || null,
            fontFamily: obj.fontFamily || null,
            fill: obj.fill || null,
            position: { x: obj.left || 0, y: obj.top || 0 },
            scale: { x: obj.scaleX || 1, y: obj.scaleY || 1 },
          },
        };
      }
      // Default case
      return {
        fieldId: obj.data?.id || `custom_${Date.now()}`,
        type: obj.type,
        imageUrl: obj.data?.originalFilePath || null,
        content: obj.data?.originalFilePath || obj.toDataURL("png"),
        properties: {
          position: { x: obj.left || 0, y: obj.top || 0 },
          scale: { x: obj.scaleX || 1, y: obj.scaleY || 1 },
        },
      };
    });

    // Generate canvas preview as data URL (this will be processed server-side)
    const previewImage = canvas.toDataURL("image/png");

    const customization = {
      template: selectedTemplate?._id || null,
      preview: previewImage, // This will be converted to file path server-side
      description: orderDescription || "",
      customFields: customFields,
      requiredFields: Object.entries(fieldInputs).map(([fieldId, value]) => {
        const fieldDef = requiredFields.find((f) => f.id === fieldId);
        const fieldType = fieldDef?.type || "text";
        const isImageField = typeof value === "object" && value.displayData;

        return {
          fieldId,
          type: fieldType,
          imageUrl: isImageField ? null : null, // Will be set server-side if needed
          value: isImageField ? value.displayData : value,
        };
      }),
    };

    await addToCart({
      product: selectedProduct,
      quantity: parseInt(quantity),
      customization,
    });
  };

  // NEW: Handler for confirming the Add-To-Cart from the modal.
  // Parameter "redirect" is true when proceeding to checkout.
  const handleConfirmAddToCart = async (redirect) => {
    try {
      await addItemToCart();
      setShowCartModal(false);

      // Play the cart sound
      cartAudio
        .play()
        .catch((err) => console.error("Error playing sound:", err));

      setShowCartNotification(true);
      if (redirect) {
        setTimeout(() => {
          setShowCartNotification(false);
          navigate("/cart");
        }, 3000);
      } else {
        setTimeout(() => {
          setShowCartNotification(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      // alert('For adding more items to your cart please log in.');
      setShowLoginModal(true);
    }
  };

  if (isLoading) {
    return <ProductLoadingSkeleton />;
  }

  return (
    <div className="container mx-auto p-4">
      {/* Header */}
      <ProductHeader selectedProduct={selectedProduct} />

      {/* Login Required Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full animate-popup">
            <div className="flex flex-col items-center text-center">
              <div className="bg-blue-100 p-4 rounded-full mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Login Required
              </h2>
              <p className="text-gray-600 mb-6">
                Please log in to add more items to your cart and continue
                shopping.
              </p>

              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={() => navigate("/login")}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Log In
                </button>

                <button
                  onClick={() => setShowLoginModal(false)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCartNotification && (
        <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ease-in-out">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex flex-col items-center space-y-4">
            <Lottie
              animationData={cartAnimation}
              loop={false}
              className="h-24 w-24"
            />
            <p className="text-xl">Item added to cart successfully!</p>
          </div>
        </div>
      )}

      {/* Overlay for mobile upload mode */}
      {showUploadOverlay && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => {
            setShowUploadOverlay(false);
            setShowStickyUpload(false);
            setDesignMode(null);
          }}
        ></div>
      )}

      {/* Sticky Upload UI for mobile */}
      {showStickyUpload && (
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-lg rounded-b-lg">
          <div className="p-4">
            <div className="flex justify-between items-center mb-2">
              {designMode === "customize" ? (
                <h3 className="font-bold text-gray-800">
                  Customize Your Design
                </h3>
              ) : (
                <h3 className="font-bold text-gray-800">Upload Your Design</h3>
              )}
              <button
                onClick={() => {
                  setShowUploadOverlay(false);
                  setShowStickyUpload(false);
                  setDesignMode(" ");
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>

            {designMode === "customize" && (
              <div className="mb-2">
                <input
                  type="text"
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-400 rounded mb-2"
                  placeholder="Enter custom text"
                />
                <button
                  onClick={handleCustomText}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Add Text
                </button>
              </div>
            )}

            {/* Simplified upload button */}
            <label
              htmlFor="mobile-file-input"
              className="flex items-center justify-center w-full p-3 border-2 border-dashed border-blue-300 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors duration-200"
            >
              <div className="text-center">
                <svg
                  className="w-8 h-8 mx-auto text-blue-500 mb-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  ></path>
                </svg>
                <span className="font-medium text-blue-700">
                  Choose an image
                </span>
                <p className="text-sm text-blue-600 mt-1">
                  PNG, JPG, GIF, WEBP, or SVG (max 15MB)
                </p>
              </div>
            </label>

            {/* Hidden original input - key attribute forces React to recreate the component */}
            <div className="hidden">
              <input
                style={{ display: "hidden" }}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  handleCustomImage(e);
                  e.target.value = "";
                }}
                className="hidden"
                id="mobile-file-input"
                key={`mobile-file-input-${uploadedImages.length}`}
              />
            </div>

            {/* Display uploaded images */}
            {uploadedImages.length > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">
                  Uploaded Images:
                </h4>
                {uploadedImages.map((img, index) => (
                  <div
                    key={index}
                    className=" flex items-center p-2 bg-white rounded mb-1 shadow-sm"
                  >
                    <svg
                      className="w-5 h-5 text-green-500 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>

                    <span className="text-gray-800">{img.name}</span>
                    <span>
                      <button
                        // onClick={handleDelete}
                        onClick={() => {
                          handleDelete();
                          handleImageDelete(index);
                        }}
                        className="text-red-600 rounded hover:bg-red-200 ml-3"
                        style={{
                          boxShadow: "none",
                          border: "none",
                          transform: "none",
                          padding: 1,
                        }}
                      >
                        X
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[59.5%_40%] gap-2"
        ref={topRef}
      >
        {/* Left Container*/}
        {/* Canvas and Product Info Column */}
        <div
          className={`bg-white p-6 rounded-lg shadow-md ${showStickyUpload ? "upload-mode-active" : ""
            } `}
        >
          {/* Main container with flex for XL screens */}
          <div className="w-full xl:flex xl:flex-row xl:gap-2">
            {/* Left side: Thumbnails for XL screens */}
            {/* <div className="hidden xl:block xl:w-28">
              {selectedProduct?.images && selectedProduct.images.length > 0 && (
                <div className="flex flex-col gap-3">
                  {reorderedImages.map((image, index) => (
                    <div
                      key={index}
                      className={`cursor-pointer border-2 rounded p-2 w-28 h-28
                      ${
                        selectedProductImage === index
                          ? "border-blue-500"
                          : "border-gray-200"
                      }
                      ${index === 1 ? "p-6" : ""}
                    `}
                      onClick={() => {
                        setSelectedProductImage(index);
                        loadProductImage(image.data, index);
                      }}
                    >
                      <img
                        src={image.data}
                        alt={`Product view ${index + 1}`}
                        className="w-full h-full object-fill rounded"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div> */}
            <div className="hidden xl:block xl:w-28">
              {processedImages && processedImages.length > 0 && (
                <div className="flex flex-col gap-3">
                  {processedImages.map((image, index) => (
                    <div
                      key={index}
                      className={`cursor-pointer border-2 rounded p-2 w-28 h-28
          ${selectedProductImage === index
                          ? "border-blue-500"
                          : "border-gray-200"
                        }
          ${index === 1 ? "p-6" : ""}
        `}
                      onClick={() => {
                        setSelectedProductImage(index);
                        loadProductImage(image.displayUrl || image.data, index);
                      }}
                    >
                      <img
                        src={
                          image.thumbnailUrl || image.displayUrl || image.data
                        }
                        alt={`Product view ${index + 1}`}
                        className="w-full h-full object-fill rounded"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right side: Canvas and mobile thumbnails */}
            <div className="flex-1">
              {/* Canvas container with responsive styling */}
              <div className="flex justify-center">
                <div id="canvas-container" className="w-full "></div>
              </div>

              {/* Mobile thumbnails - only visible on smaller screens */}
              {/* {selectedProduct?.images && selectedProduct.images.length > 0 && (
                <div className="mt-4 grid grid-cols-6 gap-2 xl:hidden">
                  {reorderedImages.map((image, index) => (
                    <div
                      key={index}
                      className={`cursor-pointer border-2 rounded p-1 ${
                        selectedProductImage === index
                          ? "border-blue-500"
                          : "border-gray-200"
                      }`}
                      onClick={() => {
                        setSelectedProductImage(index);
                        loadProductImage(image.data, index);
                      }}
                    >
                      <img
                        src={image.data}
                        alt={`Product view ${index + 1}`}
                        className="w-full h-full object-cover rounded"
                      />
                    </div>
                  ))}
                </div>
              )} */}
              {processedImages && processedImages.length > 0 && (
                <div className="mt-4 grid grid-cols-6 gap-2 xl:hidden">
                  {processedImages.map((image, index) => (
                    <div
                      key={index}
                      className={`cursor-pointer border-2 rounded p-1 ${selectedProductImage === index
                        ? "border-blue-500"
                        : "border-gray-200"
                        }`}
                      onClick={() => {
                        setSelectedProductImage(index);
                        loadProductImage(image.displayUrl || image.data, index);
                      }}
                    >
                      <img
                        src={
                          image.thumbnailUrl || image.displayUrl || image.data
                        }
                        alt={`Product view ${index + 1}`}
                        className="w-full h-full object-cover rounded"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedProduct?.note && (
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="w-7">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7 text-yellow-600 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                {/* <h3 className="text-lg font-semibold text-yellow-800">Note: </h3> */}
                <div className="ml-2 text-yellow-700 text-justify text-sm">
                  {selectedProduct.note}
                </div>
              </div>
            </div>
          )}

          {/* Customization Controls - Same for all screen sizes */}
          {selectedObject && (
            <div className="mt-4 p-4 border rounded">
              <h4 className="font-semibold mb-2">Customize Element</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={
                        selectedColor === "transparent"
                          ? "#ffffff"
                          : selectedColor
                      }
                      onChange={(e) => {
                        setSelectedColor(e.target.value);
                        updateSelectedObject();
                      }}
                      className="w-full"
                    />
                    {/* This is For making shapes Transparent */}
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedColor === "transparent"}
                        onChange={(e) => {
                          setSelectedColor(
                            e.target.checked ? "transparent" : "#000000"
                          );
                          updateSelectedObject();
                        }}
                        className="mr-2"
                      />
                      Transparent
                    </label>
                  </div>
                </div>
                {(selectedObject.type === "i-text" ||
                  selectedObject.type === "text") && (
                    <>
                      <div>
                        <label className="block text-sm mb-1">Font Size</label>
                        <select
                          value={selectedFontSize}
                          onChange={(e) => {
                            setSelectedFontSize(e.target.value);
                            updateSelectedObject();
                          }}
                          className="w-full p-2 border rounded"
                        >
                          {[12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64].map(
                            (size) => (
                              <option key={size} value={size}>
                                {size}px
                              </option>
                            )
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm mb-1">Font Family</label>
                        <select
                          value={selectedFontFamily}
                          onChange={(e) => {
                            setSelectedFontFamily(e.target.value);
                            updateSelectedObject();
                          }}
                          className="w-full p-2 border rounded"
                        >
                          {fontFamilies.map((font) => (
                            <option key={font} value={font}>
                              {font}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
              </div>
              {!selectedObject.data?.required && (
                <button
                  onClick={handleDelete}
                  className="mt-2 w-full bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                >
                  Delete Element
                </button>
              )}
            </div>
          )}

          {/* Product Info */}
          {/* Product Specifications */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h3 className="text-md font-semibold mb-3">
              Product Specifications
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-1 gap-4  text-sm">
              {/* SKU Section */}
              <div className="flex flex-nowrap items-center overflowX-hidden">
                {selectedProduct?.sku && (
                  <div className="flex items-center whitespace-nowrap mr-6">
                    <span className="text-gray-600 mr-1">SKU:</span>
                    <span className="font-medium">{selectedProduct.sku}</span>
                  </div>
                )}
                {selectedProduct?.processingTime && (
                  <div className="flex items-center whitespace-nowrap">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      <path
                        strokeLinecap="round"
                        strokeWidth="2"
                        d="M12 6v6l4 2"
                      />
                    </svg>
                    <span className="text-gray-600 mr-1">Processing Time:</span>
                    <span className="font-medium">
                      {selectedProduct.processingTime}
                    </span>
                  </div>
                )}
              </div>

              {/* Dimensions Section */}
              {(selectedProduct?.dimensions?.length ||
                selectedProduct?.dimensions?.width ||
                selectedProduct?.dimensions?.height) && (
                  <div className="col-span-2">
                    <p className="text-gray-600 mb-2">Dimensions:</p>
                    <div className="flex flex-wrap gap-1 ml-1">
                      {selectedProduct?.dimensions?.length && (
                        <div className="flex items-center text-sm">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md mr-1">
                            Length
                          </span>
                          <span className="font-normal">
                            {selectedProduct.dimensions.length} inch
                          </span>
                        </div>
                      )}

                      {selectedProduct?.dimensions?.width && (
                        <div className="flex items-center text-sm">
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md mr-1">
                            Width
                          </span>
                          <span className="font-normal ">
                            {selectedProduct.dimensions.width} inch
                          </span>
                        </div>
                      )}

                      {selectedProduct?.dimensions?.height && (
                        <div className="flex items-center text-sm">
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md mr-1">
                            Height
                          </span>
                          <span className="font-normal">
                            {selectedProduct.dimensions.height} inch
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Weight Section - Optional */}
              {selectedProduct?.weight && (
                <div className="flex items-center">
                  <span className="text-gray-600 mr-2">Weight:</span>
                  <span className="font-medium">
                    {selectedProduct.weight} g
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* right container */}
        <div
          className={`bg-white p-6 rounded-lg shadow-md space-y-6 ${showStickyUpload ? "upload-mode-inactive" : ""
            } `}
        >
          {/* Out-of-stock Get Notified (only shown if product is not in stock) */}
          {!selectedProduct?.inStock && (
            <>
              <button className="w-full bg-gray-400 text-white px-6 py-3 rounded-lg text-lg font-semibold cursor-not-allowed">
                Out of Stock
              </button>
              {!showNotifyForm ? (
                <button
                  onClick={() => {
                    setShowNotifyForm(true);
                    setTimeout(() => setAnimatingNotify(true), 50);
                  }}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  <svg
                    className="w-6 h-6 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m2 0a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  Notify me
                </button>
              ) : (
                <div
                  className="slide-down-animation overflow-hidden transition-all duration-500"
                  style={{
                    maxHeight: animatingNotify ? "500px" : "0",
                    opacity: animatingNotify ? 1 : 0,
                    transform: animatingNotify
                      ? "translateY(0)"
                      : "translateY(-20px)",
                  }}
                >
                  <div className="p-4 border rounded-lg bg-blue-50">
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">
                      Get Notified
                    </h4>
                    <p className="text-sm text-blue-700 mb-2">
                      Enter your email and/or phone number to receive a
                      notification when this product becomes available.
                    </p>
                    <div className="space-y-2">
                      <input
                        type="email"
                        placeholder="Your Email"
                        value={notifyContact.email}
                        onChange={(e) =>
                          setNotifyContact((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border rounded"
                      />
                      <input
                        type="tel"
                        placeholder="Your Phone Number"
                        value={notifyContact.phone}
                        onChange={(e) =>
                          setNotifyContact((prev) => ({
                            ...prev,
                            phone: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border rounded"
                      />
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          // First trigger the animation by setting a state
                          setFormClosing(true);
                          setAnimatingNotify(false);
                          // Then after animation completes, handle the actual submission
                          setTimeout(() => {
                            handleRemindMe();
                            setFormClosing(false);
                            setShowNotifyForm(false);
                          }, 300);
                        }}
                        className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
                      >
                        Notify Me
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Design and Customization Column */}
          {selectedProduct?.inStock && (
            <>
              <h2 className="text-lg font-bold mb-3">Design The Product</h2>
              <div className="w-full max-w-2xl mx-auto m-0 p-0 ">
                <div className="flex flex-col sm:flex-row justify-center gap-4 m-0 p-0">
                  <button
                    className=" w-full bg-indigo-600 hover:bg-blue-600 text-white font-medium px-6 py-3 rounded-md shadow-sm transition duration-5000 ease-in-out flex items-center justify-center"
                    onClick={() => {
                      setDesignMode("upload");
                      setTimeout(() => setAnimatingUpload(true), 50);
                      const isMobile = window.innerWidth < 768;
                      if (isMobile) {
                        setShowStickyUpload(true);
                        setShowUploadOverlay(true);
                        if (topRef.current) {
                          const elementPosition =
                            topRef.current.getBoundingClientRect().top +
                            window.pageYOffset;
                          const offsetPosition = elementPosition - 195;
                          window.scrollTo({
                            top: offsetPosition,
                            behavior: "smooth",
                          });
                        }
                      }
                      if (
                        selectedProductImage !== 1 &&
                        processedImages?.length > 1
                      ) {
                        setSelectedProductImage(1);
                        loadProductImage(
                          processedImages[1].displayUrl ||
                          processedImages[1].data,
                          1
                        );
                      }
                    }}
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      ></path>
                    </svg>
                    Upload Your Design
                  </button>

                  <button
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium px-6 py-3 rounded-md shadow-sm transition duration-200 ease-in-out flex items-center justify-center"
                    onClick={() => {
                      setDesignMode("customize");
                      setTimeout(() => setAnimatingCustomize(true), 50);
                      setShowTemplatesPopup(true);
                      const isMobile = window.innerWidth < 768;
                      if (isMobile) {
                        setShowStickyUpload(true);
                        setShowUploadOverlay(true);
                        if (topRef.current) {
                          const elementPosition =
                            topRef.current.getBoundingClientRect().top +
                            window.pageYOffset;
                          const offsetPosition = elementPosition - 195;
                          window.scrollTo({
                            top: offsetPosition,
                            behavior: "smooth",
                          });
                        }
                      }
                      if (
                        selectedProductImage !== 1 &&
                        processedImages?.length > 1
                      ) {
                        setSelectedProductImage(1);
                        loadProductImage(
                          processedImages[1].displayUrl ||
                          processedImages[1].data,
                          1
                        );
                      }
                    }}
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      ></path>
                    </svg>
                    Customize Your Product
                  </button>
                </div>
              </div>

              {designMode === "upload" && (
                <div
                  className={`mt-4 slide-down-animation overflow-hidden transition-all duration-500 ${showStickyUpload
                    ? "mobile-hidden-when-sticky sticky-active"
                    : "mobile-hidden-when-sticky"
                    }`}
                  style={{
                    maxHeight: animatingUpload ? "1000px" : "0",
                    opacity: animatingUpload ? 1 : 0,
                    transform: animatingUpload
                      ? "translateY(0)"
                      : "translateY(-20px)",
                  }}
                >
                  {/* Custom styled file input with proper reset */}
                  <div className="mt-4">
                    <label className="block font-medium mb-2">
                      Add Custom Image
                    </label>

                    {/* Custom styled button */}
                    <label
                      htmlFor="custom-file-input"
                      className="flex items-center justify-center w-full p-4 border-2 border-dashed border-blue-300 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors duration-200"
                    >
                      <div className="text-center">
                        <svg
                          className="w-10 h-10 mx-auto text-blue-500 mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          ></path>
                        </svg>
                        <span className="text-lg font-medium text-blue-700">
                          Choose an image
                        </span>
                        <p className="text-sm text-blue-600 mt-1">
                          PNG, JPG, GIF, WEBP, or SVG (max 15MB)
                        </p>
                      </div>
                    </label>

                    {/* Hidden original input - key attribute forces React to recreate the component */}
                    <div className="hidden">
                      <input
                        style={{ display: "hidden" }}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          handleCustomImage(e);
                          // Clear the input value by replacing the element (key changes)
                          e.target.value = "";
                        }}
                        className="hidden"
                        id="custom-file-input"
                        key={`file-input-${uploadedImages.length}`} // Forces component to reset when uploads change
                      />
                    </div>

                    {/* Display uploaded images */}
                    {uploadedImages.length > 0 && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">
                          Uploaded Images:
                        </h4>
                        {uploadedImages.map((img, index) => (
                          <div
                            key={index}
                            className=" flex items-center p-2 bg-white rounded mb-1 shadow-sm"
                          >
                            <svg
                              className="w-5 h-5 text-green-500 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                              ></path>
                            </svg>

                            <span className="text-gray-800">{img.name}</span>
                            <span>
                              <button
                                // onClick={handleDelete}
                                onClick={() => {
                                  handleDelete();
                                  handleImageDelete(index);
                                }}
                                className="text-red-600 rounded hover:bg-red-200 ml-3"
                                style={{
                                  boxShadow: "none",
                                  border: "none",
                                  transform: "none",
                                  padding: 1,
                                }}
                              >
                                X
                              </button>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {designMode === "customize" && (
                <div
                  className="slide-down-animation overflow-hidden transition-all duration-1500"
                  style={{
                    maxHeight: animatingCustomize ? "3000px" : "0",
                    opacity: animatingCustomize ? 1 : 0,
                    transform: animatingCustomize
                      ? "translateY(0)"
                      : "translateY(-20px)",
                  }}
                >
                  {/* Show selected template info or button to select template */}
                  {selectedTemplate ? (
                    <div className="p-4 bg-blue-50 rounded-lg mb-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-semibold">
                            Selected Template: {selectedTemplate.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Edit the template elements on the product
                          </p>
                        </div>
                        <button
                          onClick={toggleTemplatesPopup}
                          className="px-3 py-1 text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg mb-4">
                      <div className="flex justify-between items-center">
                        <p className="text-gray-600">No template selected</p>
                        <button
                          onClick={toggleTemplatesPopup}
                          className="px-3 py-1 text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                        >
                          Choose Template
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedTemplate && requiredFields.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">
                        Required Template Fields
                      </h3>
                      {requiredFields.map((field) => (
                        <div
                          key={field.id}
                          className="p-4 bg-gray-50 rounded-lg"
                        >
                          <label className="block font-medium mb-2">
                            {field.type.charAt(0).toUpperCase() +
                              field.type.slice(1)}
                            {field.placeholder && ` - ${field.placeholder}`}
                          </label>
                          {field.type === "text" ? (
                            <input
                              type="text"
                              value={fieldInputs[field.id] || ""}
                              onChange={(e) =>
                                handleFieldInput(
                                  field.id,
                                  e.target.value,
                                  "text"
                                )
                              }
                              className="w-full px-3 py-2 border rounded"
                              placeholder={
                                field.placeholder || `Enter ${field.type}`
                              }
                            />
                          ) : (
                            <div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFieldInput(
                                    field.id,
                                    e.target.files[0],
                                    field.type
                                  )
                                }
                                className="w-full"
                              />
                              <p className="text-sm text-gray-500 mt-1">
                                Click the placeholder on the design to replace
                                with your {field.type}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div>
                    {/* <h3 className="text-lg font-semibold mb-3">Add Custom Elements</h3> */}
                    <div className="space-y-3">
                      {/* Custom styled file input with proper reset */}
                      <div className="mt-4">
                        <label className="block font-medium mb-2">
                          Add Custom Image
                        </label>

                        {/* Custom styled button */}
                        <label
                          htmlFor="custom-file-input"
                          className="flex items-center justify-center w-full p-4 border-2 border-dashed border-blue-300 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors duration-200"
                        >
                          <div className="text-center">
                            <svg
                              className="w-10 h-10 mx-auto text-blue-500 mb-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              ></path>
                            </svg>
                            <span className="text-lg font-medium text-blue-700">
                              Choose an image
                            </span>
                            <p className="text-sm text-blue-600 mt-1">
                              PNG, JPG, GIF, WEBP, or SVG (max 15MB)
                            </p>
                          </div>
                        </label>

                        {/* Hidden original input - key attribute forces React to recreate the component */}
                        <div className="hidden">
                          <input
                            style={{ display: "hidden" }}
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              handleCustomImage(e);
                              // Clear the input value by replacing the element (key changes)
                              e.target.value = "";
                            }}
                            className="hidden"
                            id="custom-file-input"
                            key={`file-input-${uploadedImages.length}`} // Forces component to reset when uploads change
                          />
                        </div>

                        {/* Display uploaded images */}
                        {uploadedImages.length > 0 && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <h4 className="font-medium text-gray-700 mb-2">
                              Uploaded Images:
                            </h4>
                            {uploadedImages.map((img, index) => (
                              <div
                                key={index}
                                className=" flex items-center p-2 bg-white rounded mb-1 shadow-sm"
                              >
                                <svg
                                  className="w-5 h-5 text-green-500 mr-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  ></path>
                                </svg>

                                <span className="text-gray-800">
                                  {img.name}
                                </span>
                                <span>
                                  <button
                                    // onClick={handleDelete}
                                    onClick={() => {
                                      handleDelete();
                                      handleImageDelete(index);
                                    }}
                                    className="text-red-600 rounded hover:bg-red-200 ml-3"
                                    style={{
                                      boxShadow: "none",
                                      border: "none",
                                      transform: "none",
                                      padding: 1,
                                    }}
                                  >
                                    X
                                  </button>
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div style={{ marginTop: 30 + "px" }}>
                        <input
                          type="text"
                          value={customText}
                          onChange={(e) => setCustomText(e.target.value)}
                          className="w-full px-3 py-2 border rounded mb-2"
                          placeholder="Enter custom text"
                        />
                        <button
                          onClick={handleCustomText}
                          className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                        >
                          Add Text
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg mt-4">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Design Instructions
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Select a template or add custom elements</li>
                      <li>
                        • Fill in all required fields for the selected template
                      </li>
                      <li>• Click any element to customize it</li>
                      <li>• Double-click text to edit directly</li>
                      <li>• Drag elements to reposition</li>
                      <li>• Use corners to resize elements</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Always show Quantity and Special Instructions */}
              <div
                className="space-y-4 slide-down-animation overflow-hidden transition-all duration-1000"
                style={{
                  maxHeight: animatingCustomize ? "3000px" : "0",
                  opacity: animatingCustomize ? 1 : 0,
                  transform: animatingCustomize
                    ? "translateY(0)"
                    : "translateY(-20px)",
                }}
              >
                <div>
                  <label className="block font-medium mb-2">Quantity</label>
                  <div className="flex items-center gap-2">
                    {selectedProduct?.pricingTiers?.length > 0 ? (
                      <select
                        value={quantity}
                        onChange={(e) =>
                          handleQuantityChange(parseInt(e.target.value))
                        }
                        className="w-56 px-3 py-2 border rounded"
                        disabled={!selectedProduct?.inStock}
                      >
                        {getBundleOptions().map((option) => (
                          <option key={option} value={option}>
                            {option === 20000 ? `+${option}` : option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="number"
                        min={selectedProduct?.minimumOrder || 1}
                        max="20000"
                        value={quantity}
                        onChange={(e) =>
                          handleQuantityChange(
                            parseInt(e.target.value) ||
                            selectedProduct?.minimumOrder ||
                            1
                          )
                        }
                        className="w-full px-3 py-2 border rounded"
                        disabled={!selectedProduct?.inStock}
                      />
                    )}
                    {selectedProduct?.pricingTiers?.length > 0 &&
                      (quantity === 20000 ? (
                        // <div style={{minWidth: '250px'}}>
                        //   <div className="mt-2 p-4 bg-blue-100 border border-blue-300 text-blue-700 rounded-lg shadow-md">
                        //     <p className="font-semibold text-lg">We'll Contact You</p>
                        //     <p className="text-sm">To provide the best deal for you</p>
                        //   </div>
                        // </div>
                        <span className="text-sm text-gray-500">
                          We'll Offer Best Deals
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">
                          subTotal: ${(quantity * currentUnitPrice).toFixed(2)}
                          <br /> (${currentUnitPrice}/unit)
                        </span>
                      ))}
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-2">
                    Special Instructions
                  </label>
                  <textarea
                    value={orderDescription}
                    onChange={(e) => setOrderDescription(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Add any special requirements or notes..."
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* NEW: Add To Cart Button (shown at the end of the page) for in-stock products */}
      {selectedProduct?.inStock && (
        <div className="mt-8">
          <button
            onClick={openCartModal}
            className="w-full bg-green-500 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-green-600 transition-colors duration-200"
          >
            {quantity >= 20000
              ? "Add to Cart"
              : `Add to Cart - $${(currentUnitPrice * quantity).toFixed(2)}`}
          </button>
        </div>
      )}

      {/* NEW: Modal for Add-To-Cart confirmation */}
      {showCartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full relative">
            {/* Close button */}
            <button
              onClick={() => setShowCartModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {/* Header */}
            <div className="border-b pb-4 mb-4">
              <h3 className="text-xl font-bold text-gray-800">Order Summary</h3>
            </div>

            {/* Content */}
            <div className="space-y-4">
              {/* Product info could go here */}

              {/* Total */}
              {quantity < 20000 && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">
                      ${(currentUnitPrice * quantity).toFixed(2)}
                    </span>
                  </div>
                  {(selectedProduct?.hasGST || selectedProduct?.hasPST) && (
                    <div className="mt-2 text-sm text-gray-500 border-t pt-2">
                      <div className="flex justify-between items-center">
                        <span>Taxes:</span>
                        <span>
                          {[
                            selectedProduct.hasGST && "GST",
                            selectedProduct.hasPST && "PST",
                          ]
                            .filter(Boolean)
                            .join(" + ")}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col space-y-3 mt-6">
              <button
                onClick={() => handleConfirmAddToCart(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-md font-medium flex items-center justify-center transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                </svg>
                Proceed to Checkout
              </button>

              <button
                onClick={() => handleConfirmAddToCart(false)}
                className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-3 rounded-md font-medium flex items-center justify-center transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}

      {remindPopup.show && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white border rounded-lg shadow-lg p-6">
            <p className="text-lg text-gray-800">{remindPopup.message}</p>
            <button
              onClick={() => setRemindPopup({ show: false, message: "" })}
              className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Templates Selection Popup */}
      {showTemplatesPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 template-popup-container">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-3xl w-full max-h-90vh overflow-y-auto relative animate-popup">
            {/* Close button */}
            <button
              onClick={toggleTemplatesPopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {/* Header */}
            <div className="border-b pb-4 mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                Choose a Template
              </h3>
              <p className="text-gray-600 mt-1">
                Select a template or continue without one
              </p>
            </div>

            {/* First view - Group Selection */}
            {showGroupSelection ? (
              <div>
                <h3 className="text-lg font-medium mb-4">
                  Select a Template Group
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div
                    onClick={() => handleGroupSelect(null, "All Templates")}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 cursor-pointer text-center"
                  >
                    <div className="mb-2 inline-flex items-center justify-center bg-blue-100 w-12 h-12 rounded-full">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      </svg>
                    </div>
                    <p className="font-medium">All Templates</p>
                  </div>

                  {templateGroups &&
                    templateGroups.map((group) => (
                      <div
                        key={group._id}
                        onClick={() => handleGroupSelect(group._id, group.name)}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 cursor-pointer text-center"
                      >
                        <div className="mb-2 inline-flex items-center justify-center bg-green-100 w-12 h-12 rounded-full">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 text-green-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                          </svg>
                        </div>
                        <p className="font-medium">{group.name}</p>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div>
                {/* Back button */}
                <button
                  onClick={backToGroups}
                  style={{
                    boxShadow: "none",
                    border: "none",
                    transform: "none",
                  }}
                  className="mb-4 flex items-center text-blue-600 hover:text-blue-800"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Back to Groups
                </button>

                <h3 className="text-lg font-medium mb-4">{currentGroupName}</h3>

                {/* Display templates for selected group */}
                {/* Templates Grid */}
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    {getDisplayedTemplates().map((template) => (
                      <div
                        key={template._id}
                        className={`p-3 border-2 rounded cursor-pointer hover:border-blue-300 transition-colors ${selectedTemplate?._id === template._id
                          ? "border-blue-500 ring-2 ring-blue-300"
                          : "border-gray-200"
                          } ${template.isNewlyAdded ? "fade-in-up" : ""}`}
                        style={
                          template.isNewlyAdded
                            ? {
                              animationDelay: `${template.isNewlyAdded
                                ? 0.05 *
                                (filteredTemplates.indexOf(template) -
                                  templatesPerPage)
                                : 0
                                }s`,
                            }
                            : {}
                        }
                        onClick={() => {
                          handleTemplateChange(template);
                          toggleTemplatesPopup();
                        }}
                      >
                        <img
                          src={
                            template.previewUrl ||
                            template.preview ||
                            (template.preview &&
                              template.preview.startsWith("/")
                              ? `/api${template.preview}`
                              : template.preview)
                          }
                          alt={template.name}
                          className="w-full h-32 object-contain rounded mb-2"
                        />
                        <div className="flex flex-col">
                          <p className="text-center font-medium">
                            {template.name}
                          </p>
                          {template.group && template.group.name && (
                            <span className="text-xs text-center text-blue-600">
                              {template.group.name}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Show More button - only display if there are more templates to show */}
                  {!showAllTemplates &&
                    filteredTemplates.length > templatesPerPage && (
                      <div className="flex justify-center mt-2 mb-6">
                        <button
                          onClick={handleShowMore}
                          className="px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors flex items-center"
                        >
                          <span>Show More</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 ml-1"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Templates Selection Popup - Groups section */}
            <div className="mb-4 mt-4 border-b pb-4">
              <h4 className="font-medium text-gray-700 mb-2">
                Filter by Group
              </h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => filterTemplatesByGroup(null)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${selectedGroupId === null
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  All Templates
                </button>
                {templateGroups && templateGroups.length > 0 ? (
                  templateGroups.map((group) => (
                    <button
                      key={group._id}
                      onClick={() => filterTemplatesByGroup(group._id)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${selectedGroupId === group._id
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                    >
                      {group.name}
                    </button>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">
                    No groups available
                  </span>
                )}
              </div>
            </div>

            {/* Continue without template button */}
            <div className="flex justify-center border-t pt-4">
              <button
                onClick={() => {
                  // Clear any selected template
                  setSelectedTemplate(null);
                  setRequiredFields([]);
                  toggleTemplatesPopup();
                }}
                className="px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors"
              >
                Continue without a template
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
      @keyframes slideDown {
        from {
          max-height: 0;
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          max-height: 1000px;
          opacity: 1;
          transform: translateY(0);
        }
      }
      .slide-down-animation {
        overflow: hidden;
        animation: slideDown 0.3s ease-out forwards;
      }
      .mobile-hidden-when-sticky {
        display: block;
      }
       @media (max-width: 767px) {
        .customize-mode .mobile-hidden-when-sticky.sticky-active,
        .upload-mode .mobile-hidden-when-sticky.sticky-active {
          display: none;
        }
      }
      .upload-mode-active {
        position: relative;
        z-index: 30;
        background: white;
      }

      .upload-mode-inactive {
        filter: blur(1px);
        opacity: 0.7;
        pointer-events: none;
      }

      @media (min-width: 768px) {
        .upload-mode-inactive {
          filter: none;
          opacity: 1;
          pointer-events: auto;
        }
      }
      @keyframes popIn {
        0% {
          opacity: 0;
          transform: scale(0.9);
        }
        100% {
          opacity: 1;
          transform: scale(1);
        }
      }

      .animate-popup {
        animation: popIn 0.3s ease-out forwards;
      }

      .max-h-90vh {
        max-height: 90vh;
      }

      .template-popup-container {
        padding: 1rem;
      }
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .fade-in-up {
        animation: fadeInUp 0.8s ease-out forwards;
      }
      #canvas-container {
        transition: width 0.3s ease, height 0.3s ease;
        margin: 0 auto;
        box-sizing: border-box;
      }

      /* Extra large screens */
      @media (min-width: 1551px) {
        #canvas-container {
          width: 600px !important;
          height: 600px !important;
        }
      }

      /* Large screens */
      @media (min-width: 1280px) and (max-width: 1550px) {
        #canvas-container {
          width: 500px !important;
          height: 500px !important;
        }
      }

      /* Medium-large screens */
      @media (min-width: 1224px) and (max-width: 1279px) {
        #canvas-container {
          width: 400px !important;
          height: 400px !important;
        }
      }

      /* Medium screens */
      @media (min-width: 768px) and (max-width: 1223px) {
        #canvas-container {
          width: 300px !important;
          height: 300px !important;
        }
      }

      /* Small screens */
      @media (max-width: 767px) {
        #canvas-container {
          width: calc(100vw - 50px) !important;
          height: calc(100vw - 50px) !important;
          max-width: 100%;
        }
      }
      `}</style>
    </div>
  );
};

export default ProductCustomization;